import ReactECharts, { type EChartsOption } from "echarts-for-react";
import { get } from "lodash";
import { useMemo } from "react";
import { useTriviaContext } from "../context-trivia";
import { createRandomResults } from "./random-result-generator";

export function TriviaAnswerChart() {
  const { screen, questionIndex, questionChoiceIndexes, analytics } =
    useTriviaContext();

  const buttonColorBackground = useMemo<string>(
    () => get(screen, "flyout.post.customData.buttonColorBackground"),
    [screen],
  );
  const buttonColorText = useMemo<string>(
    () => get(screen, "flyout.post.customData.buttonColorText"),
    [screen],
  );
  const answers = useMemo<{ answer: string; correct: boolean }[]>(() => {
    let a: { answer: string; correct: boolean }[] = get(
      screen,
      `flyout.post.customData.trivia.questions[${questionIndex}].answers`,
      [],
    );

    return a;
  }, [screen, questionIndex]);
  const answerChosen = useMemo<string | undefined>(() => {
    let a: string = get(
      screen,
      `flyout.post.customData.trivia.questions[${questionIndex}].answers[${questionChoiceIndexes?.[questionIndex] ?? 0}].answer`,
      [],
    );

    return a;
  }, [questionChoiceIndexes, questionIndex, screen]);

  const results = useMemo<number[]>(() => {
    const countByAnswer: Record<string, number> = {};
    const results: number[] = createRandomResults(answers);

    if (analytics?.length) {
      analytics.forEach((event) => {
        const analyticsQuestion = event.payload?.[0];
        if (analyticsQuestion?.__typename === "EventPayloadTriviaQuestion") {
          if (analyticsQuestion?.answer) {
            countByAnswer[analyticsQuestion.answer] =
              (countByAnswer[analyticsQuestion.answer] || 0) + 1;
          }
        }
      });
    }

    if (answerChosen) {
      countByAnswer[answerChosen] = (countByAnswer[answerChosen] || 0) + 1;
    }

    answers.forEach((answer, index) => {
      results[index] =
        (results[index] || 0) + (countByAnswer[answer.answer] || 0);
    });

    return results;
  }, [analytics, answerChosen, answers]);

  const option = useMemo<EChartsOption>(
    () => ({
      xAxis: [
        {
          type: "value",
          max: 100,
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          data: answers.map((answer) => answer.answer),
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      series: [
        {
          type: "bar",
          stack: "chart",
          barCategoryGap: 50,
          label: {
            position: [0, -40],
            formatter: "{b}",
            fontSize: 28,
            color: buttonColorText,
            show: true,
          },
          itemStyle: {
            borderRadius: [0, 20, 20, 0],
          },
          data: answers.map((answer, index) => ({
            value: results[index] ?? 0,
            itemStyle: {
              color: answer.correct ? "#0a7d00" : "#7d0000",
            },
          })),
        },
        {
          type: "bar",
          stack: "chart",
          barCategoryGap: 50,
          itemStyle: {
            color: "transparent",
          },
          label: {
            position: "insideRight",
            fontSize: 28,
            color: buttonColorText,
            formatter: function (params: { value: number }) {
              return `${params.value}%`;
            },
            show: true,
          },
          data: results,
        },
      ],
    }),
    [answers, buttonColorText, results],
  );

  return (
    <div
      className="relative h-full w-full rounded-3xl"
      style={{ backgroundColor: buttonColorBackground }}
    >
      <ReactECharts
        option={option}
        opts={{
          renderer: "canvas",
        }}
        style={{
          height: "100%",
          width: "100%",
        }}
      />
    </div>
  );
}
